import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGrievanceDashboard,
  useGrievanceOfficerWorkbench,
} from "../hooks";
import type {
  GrievanceDashboardResponseDto,
  GrievanceOfficerWorkbenchResponseDto,
} from "../grievance.types";


const GrievanceDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isOfficerView = staffSubRole === "GRIEVANCE_OFFICER";

  const headQuery = useGrievanceDashboard({
    enabled: !isOfficerView,
  });
  const officerQuery = useGrievanceOfficerWorkbench({
    enabled: isOfficerView,
  });

  if (isOfficerView) {
    const { data, isLoading, error } = officerQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Grievance Officer Workbench"
          message="Loading assigned complaints and resolution tasks…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Grievance Officer Workbench"
          message="Unable to load grievance dashboard. Please try again."
        />
      );
    }

    const workbench: GrievanceOfficerWorkbenchResponseDto = {
      grievanceTasks: {
        newGrievancesAssigned: data.grievanceTasks?.newGrievancesAssigned ?? 0,
        highPriorityGrievancesAssigned:
          data.grievanceTasks?.highPriorityGrievancesAssigned ?? 0,
        grievancesNearingSlaBreaches:
          data.grievanceTasks?.grievancesNearingSlaBreaches ?? 0,
      },
      feedbackTasks: {
        feedbackItemsToReview: data.feedbackTasks?.feedbackItemsToReview ?? 0,
        followUpsToComplete: data.feedbackTasks?.followUpsToComplete ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Grievance Officer Workbench"
          message="Assigned complaints, SLA follow-up, and trust recovery actions."
        />

        <SectionCard title="Complaint handling">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="New grievances assigned"
              value={workbench.grievanceTasks.newGrievancesAssigned}
            />
            <StatCard
              label="High-priority grievances"
              value={workbench.grievanceTasks.highPriorityGrievancesAssigned}
            />
            <StatCard
              label="Open SLA-risk cases"
              value={workbench.grievanceTasks.grievancesNearingSlaBreaches}
            />
          </div>
        </SectionCard>

        <SectionCard title="Feedback follow-up">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Feedback items to review"
              value={workbench.feedbackTasks.feedbackItemsToReview}
            />
            <StatCard
              label="Follow-ups to complete"
              value={workbench.feedbackTasks.followUpsToComplete}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title="Grievance & Feedback Dashboard"
        message="Loading complaints and feedback overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title="Grievance & Feedback Dashboard"
        message="Unable to load grievance dashboard. Please try again."
      />
    );
  }

  const dashboard: GrievanceDashboardResponseDto = {
    kpis: {
      openGrievances: data.kpis?.openGrievances ?? 0,
      highPriorityGrievances: data.kpis?.highPriorityGrievances ?? 0,
      feedbackItemsThisMonth: data.kpis?.feedbackItemsThisMonth ?? 0,
      grievancesResolvedThisMonth:
        data.kpis?.grievancesResolvedThisMonth ?? 0,
    },
    channels: {
      appGrievances: data.channels?.appGrievances ?? 0,
      webGrievances: data.channels?.webGrievances ?? 0,
      phoneGrievances: data.channels?.phoneGrievances ?? 0,
    },
    feedback: {
      positiveFeedback: data.feedback?.positiveFeedback ?? 0,
      neutralFeedback: data.feedback?.neutralFeedback ?? 0,
      negativeFeedback: data.feedback?.negativeFeedback ?? 0,
    },
    sla: {
      withinSlaGrievances: data.sla?.withinSlaGrievances ?? 0,
      breachedSlaGrievances: data.sla?.breachedSlaGrievances ?? 0,
    },
  };

  const { kpis, channels, feedback, sla } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title="Grievance & Feedback Dashboard"
        message="Complaints intake, resolution, and feedback trends."
      />

      <SectionCard title="Grievance KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Open grievances"
            value={kpis.openGrievances}
            helpText={`High priority: ${kpis.highPriorityGrievances}`}
          />
          <StatCard
            label="Feedback items (month)"
            value={kpis.feedbackItemsThisMonth}
            helpText={`Resolved this month: ${kpis.grievancesResolvedThisMonth}`}
          />
          <StatCard
            label="High-priority grievances"
            value={kpis.highPriorityGrievances}
          />
        </div>
      </SectionCard>

      <SectionCard title="Complaints pipeline">
        <InfoCard
          title="Complaints by channel"
          body={`App grievances: ${channels.appGrievances}, Web grievances: ${channels.webGrievances}, Phone grievances: ${channels.phoneGrievances}.`}
        />
      </SectionCard>

      <SectionCard title="Feedback & trust">
        <InfoCard
          title="Feedback trends"
          body={`Positive feedback: ${feedback.positiveFeedback}, Neutral feedback: ${feedback.neutralFeedback}, Negative feedback: ${feedback.negativeFeedback}.`}
        />
        <InfoCard
          title="SLA performance"
          body={`Within SLA: ${sla.withinSlaGrievances}, Breached SLA: ${sla.breachedSlaGrievances}.`}
        />
      </SectionCard>
    </div>
  );
};


export default GrievanceDashboardPage;